import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { getSuggestionsMap } from "@/pages/Private/pages/List/utils/column-utils";
import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";
import { EventOutreachSchemaType } from "@/pages/Private/pages/List/schema/list";
import {
	buildVariableRows,
	buildVariableToken,
	isSenderVariableKey,
	normalizeVariableSearch,
	VariableRow,
} from "@/pages/Private/pages/Messages/components/Variables/utils/variableRows";

import { FooterPopoverItem, FooterPopoverSection } from "../utils/editorFooter.types";

interface UseVariableFooterItemsArgs {
	listColumns?: ColumnConfig[];
	/** The list's event-outreach row — what makes the {event_*} and custom tokens exist at all. */
	listEvent?: EventOutreachSchemaType | null;
	currentLeadIndex?: number;
	search: string;
	onInsert: (token: string) => void;
}

const toItem = (row: VariableRow, onInsert: (token: string) => void): FooterPopoverItem => ({
	key: row.key,
	name: row.label,
	meta: row.sampleValue || undefined,
	onSelect: () => onInsert(buildVariableToken(row.key, row.label)),
});

/**
 * Lead / Event / Sender variable sections of the footer's "Variable" popover.
 *
 * The Event section only exists while the list event is passed in *and* enabled — the same
 * condition under which the send substitutes those tokens. It was missing entirely before
 * PREC-4010 because the hook never received the event, so the footer offered neither the fixed
 * {event_*} tokens nor the list's custom variables.
 */
export const useVariableFooterItems = ({
	listColumns,
	listEvent,
	currentLeadIndex = 0,
	search,
	onInsert,
}: UseVariableFooterItemsArgs): FooterPopoverSection[] => {
	const { t } = useTranslation();
	const { inbox } = useAppSelector(listSelector);

	const currentPerson = inbox[currentLeadIndex];

	const suggestionsMap = useMemo(
		() =>
			currentPerson
				? getSuggestionsMap(currentPerson, true, undefined, listEvent)
				: getSuggestionsMap(undefined, true, listColumns, listEvent),
		[currentPerson, listColumns, listEvent]
	);

	const rows = useMemo(
		() =>
			buildVariableRows({
				suggestionsMap,
				currentPerson,
				listColumns: listColumns ?? currentPerson?.list?.columns,
				listEvent,
				usedVarNames: [],
				hasRealLead: Boolean(currentPerson),
			}),
		[suggestionsMap, currentPerson, listColumns, listEvent]
	);

	const hasEventRows = useMemo(() => rows.some((row) => row.category === "event"), [rows]);

	return useMemo(() => {
		const normalizedSearch = normalizeVariableSearch(search);

		const filtered = rows.filter(
			(row) =>
				!normalizedSearch ||
				row.label.toLowerCase().includes(normalizedSearch) ||
				row.sampleValue.toLowerCase().includes(normalizedSearch)
		);

		const leadItems = filtered
			.filter((row) => row.category !== "event" && !isSenderVariableKey(row.key))
			.map((row) => toItem(row, onInsert));
		const eventItems = filtered
			.filter((row) => row.category === "event")
			.map((row) => toItem(row, onInsert));
		const senderItems = filtered
			.filter((row) => isSenderVariableKey(row.key))
			.map((row) => toItem(row, onInsert));

		const sections: FooterPopoverSection[] = [
			{ label: t("editorFooter.sections.lead"), items: leadItems },
		];

		// An empty Event section would read as "this list has an event with no fields" — omit it
		// instead, the way the Variables panel omits its event card.
		if (hasEventRows) {
			sections.push({ label: t("editorFooter.sections.event"), items: eventItems });
		}

		sections.push({ label: t("editorFooter.sections.sender"), items: senderItems });

		return sections;
	}, [rows, hasEventRows, search, onInsert, t]);
};
